import React, { useEffect, useState } from "react";
import { parseISO, isValid, format as fmt } from "date-fns";
import dayjs from "dayjs";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import jsPDF from "jspdf";
import "jspdf-autotable";
import {
  Card,
  Row,
  Col,
  Statistic,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Switch,
  Button,
  Space,
  Tag,
  Table,
  Typography,
  Modal,
  Radio,
} from "antd";
import {
  SearchOutlined,
  FilterOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  ReloadOutlined,
  UserOutlined,
  WalletOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  ExclamationCircleOutlined,
  EditOutlined,
} from "@ant-design/icons";

import Header from "../../components/Header";
import Layout from "../../layout/Layout";
import fetchData from "../../functions/fetchData";
import updateData from "../../functions/updateData";
import { fetchComboData } from "../../services/api";

const { Text } = Typography;
const { RangePicker } = DatePicker;

export default function SaleLedger() {
  const [data, setData] = useState([]);
  const [Alldata, setAllData] = useState([]);
  const [customerdata, setCustomerdata] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [Totals, setTotals] = useState({ credit: 0, debit: 0, balance: 0 });
  const [searchTerm, setSearchTerm] = useState('');

  const [adjustModalOpen, setAdjustModalOpen] = useState(false);
  const [adjustingRecord, setAdjustingRecord] = useState(null);
  const [adjustType, setAdjustType] = useState("debit");
  const [adjustAmount, setAdjustAmount] = useState(0);
  const [adjustSaving, setAdjustSaving] = useState(false);

  const [formdata, setFormData] = useState({
    from: "",
    to: "",
    customername: "",
    account_id: "",
    showOnlyFinalBalance: false
  });

  const formatDateSafe = (dateStr) => {
    const date = parseISO(dateStr);
    return isValid(date) ? fmt(date, "yyyy-MM-dd") : "";
  };

  const formatDisplayDate = (dateStr) => {
    const date = parseISO(dateStr);
    return isValid(date) ? fmt(date, "dd/MM/yyyy") : "";
  };

  const resetFilters = () => {
    setFormData({
      from: "",
      to: "",
      customername: "",
      account_id: "",
      showOnlyFinalBalance: false
    });
    setSearchTerm('');
    setData(Alldata);
  };

  const openAdjustModal = (record) => {
    const debit = Number(record.debit_amount) || 0;
    const credit = Number(record.credit_amount) || 0;
    setAdjustingRecord(record);
    setAdjustType(credit > 0 && debit === 0 ? "credit" : "debit");
    setAdjustAmount(credit > 0 && debit === 0 ? credit : debit);
    setAdjustModalOpen(true);
  };

  const closeAdjustModal = () => {
    setAdjustModalOpen(false);
    setAdjustingRecord(null);
    setAdjustAmount(0);
  };

  const handleSaveAdjustment = async () => {
    if (!adjustingRecord) return;
    const amount = Number(adjustAmount) || 0;
    if (amount < 0) {
      toast.error("Amount cannot be negative");
      return;
    }

    setAdjustSaving(true);
    try {
      const updatedFields = adjustType === "debit"
        ? { debit_amount: amount, credit_amount: 0 }
        : { debit_amount: 0, credit_amount: amount };

      await updateData("ledger_entries", updatedFields, { id: adjustingRecord.id });

      const updatedAllData = Alldata.map((r) =>
        r.id === adjustingRecord.id ? { ...r, ...updatedFields } : r
      );
      setAllData(updatedAllData);

      toast.success(`Ledger entry #${adjustingRecord.transaction_id} adjusted successfully`);
      closeAdjustModal();
    } catch (err) {
      console.error("Error adjusting ledger entry:", err);
      toast.error("Failed to adjust ledger entry");
    } finally {
      setAdjustSaving(false);
    }
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    let currentY = 22;

    doc.setFontSize(18);
    doc.text('Customer Ledger Report', 14, currentY);
    currentY += 8;

    doc.setFontSize(10);
    doc.text(`Generated on: ${fmt(new Date(), "dd/MM/yyyy HH:mm")}`, 14, currentY);
    currentY += 6;

    // Add customer name if selected
    if (formdata.customername) {
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text(`Customer: ${formdata.customername}`, 14, currentY);
      doc.setFont(undefined, 'normal');
      currentY += 6;
    }

    // Add date range if selected
    if (formdata.from || formdata.to) {
      doc.setFontSize(10);
      const dateRange = `Period: ${formdata.from ? formatDisplayDate(formdata.from) : 'Start'} to ${formdata.to ? formatDisplayDate(formdata.to) : 'End'}`;
      doc.text(dateRange, 14, currentY);
      currentY += 6;
    }

    const tableColumn = ["Txn ID", "Date", "A/C Type", "Description", "Debit", "Credit", "Balance"];
    const tableRows = [];

    data.forEach((row) => {
      const debit = Number(row.debit_amount) || 0;
      const credit = Number(row.credit_amount) || 0;

      tableRows.push([
        row.transaction_id,
        formatDisplayDate(row.date),
        row.account_type,
        row.description,
        debit.toFixed(2),
        credit.toFixed(2),
        (row.running_balance ?? 0).toFixed(2)
      ]);
    });

    tableRows.push([
      "TOTALS",
      "",
      "",
      "",
      Totals.debit.toFixed(2),
      Totals.credit.toFixed(2),
      Totals.balance.toFixed(2)
    ]);

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: currentY + 2,
      theme: "striped",
      headStyles: { fillColor: [41, 128, 185] },
      styles: { fontSize: 8 },
      didParseCell: function(data) {
        // Check if this is the totals row (last row)
        if (data.row.index === tableRows.length - 1) {
          data.cell.styles.fillColor = [240, 248, 255]; // Soft blue background (Alice Blue)
          data.cell.styles.textColor = [0, 0, 0]; // Black text
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 10; // Larger font size
          data.cell.styles.lineWidth = 0.5;
          data.cell.styles.lineColor = [100, 149, 237]; // Cornflower blue border
        }
      }
    });

    doc.save(`ledger_report_${fmt(new Date(), "ddMMyyyy")}.pdf`);
    toast.success("PDF exported successfully!");
  };

  const exportToCSV = () => {
    const headerRows = [
      { transaction_id: 'Customer Ledger Report', date: '', customer_name: '', account_type: '', account_id: '', description: '', debit_amount: '', credit_amount: '', running_balance: '' },
      ...(formdata.customername ? [{ transaction_id: `Customer: ${formdata.customername}`, date: '', customer_name: '', account_type: '', account_id: '', description: '', debit_amount: '', credit_amount: '', running_balance: '' }] : []),
      { transaction_id: `Generated: ${fmt(new Date(), "dd/MM/yyyy HH:mm")}`, date: '', customer_name: '', account_type: '', account_id: '', description: '', debit_amount: '', credit_amount: '', running_balance: '' },
      { transaction_id: '', date: '', customer_name: '', account_type: '', account_id: '', description: '', debit_amount: '', credit_amount: '', running_balance: '' },
    ];

    const dataRows = data.map((row) => ({
      transaction_id: row.transaction_id,
      date: formatDisplayDate(row.date),
      customer_name: row.customer_name,
      account_type: row.account_type,
      account_id: row.account_id,
      description: row.description,
      debit_amount: row.debit_amount,
      credit_amount: row.credit_amount,
      running_balance: (row.running_balance ?? 0).toFixed(2)
    }));

    const rows = [...headerRows, ...dataRows];
    const headers = Object.keys(rows[0]);
    const csvLines = [
      headers.join(","),
      ...rows.map(row => headers.map(h => `"${String(row[h] ?? "").replace(/"/g, '""')}"`).join(",")),
    ];
    const blob = new Blob([csvLines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `ledger_report_${fmt(new Date(), "ddMMyyyy")}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const fetched = await fetchData("ledger_entries", null, "id", {});
        setAllData(fetched);
        setData(fetched);

        const result = await fetchComboData("customers", "name");
        setCustomerdata(result);
      } catch (e) {
        console.error("Error fetching data:", e);
        toast.error("Failed to load data");
      }
      setLoading(false);
    };
    loadData();
  }, []);

  useEffect(() => {
    const filterData = () => {
      const { from, to, account_id, showOnlyFinalBalance } = formdata;
      let filtered = [...Alldata];

      // Apply search term filter
      if (searchTerm) {
        filtered = filtered.filter(r =>
          r.transaction_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          r.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          r.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          r.account_id?.toString().includes(searchTerm)
        );
      }

      // Date range filter
      const safeFrom = formatDateSafe(from);
      const safeTo = formatDateSafe(to);
      if (safeFrom && safeTo) {
        filtered = filtered.filter((r) => {
          const d = formatDateSafe(r.date);
          return d && d >= safeFrom && d <= safeTo;
        });
      }

      // Customer filter - show ALL account types for this customer
      if (account_id) {
        filtered = filtered.filter((r) => r.account_id === parseInt(account_id));
      }

      // Apply final balance filter if enabled
      if (showOnlyFinalBalance) {
        // Group by customer (account_id) and calculate final balance for each
        const customerBalances = {};
        filtered.forEach(r => {
          const customerId = r.account_id;
          if (!customerBalances[customerId]) {
            // Find customer name from customerdata array
            const customerInfo = customerdata.find(c => c.id === parseInt(customerId));
            customerBalances[customerId] = {
              ...r,
              customer_name: customerInfo?.name || r.customer_name || 'Unknown Customer',
              debit_amount: 0,
              credit_amount: 0,
              final_balance: 0
            };
          }
          customerBalances[customerId].debit_amount += Number(r.debit_amount) || 0;
          customerBalances[customerId].credit_amount += Number(r.credit_amount) || 0;
        });

        // Calculate final balance and create summary records
        filtered = Object.values(customerBalances).map(customer => ({
          ...customer,
          final_balance: customer.debit_amount - customer.credit_amount,
          description: `Final Balance for ${customer.customer_name}`,
          transaction_id: 'BALANCE_SUMMARY'
        }));
      }

      const credit = filtered.reduce((sum, r) => sum + (Number(r.credit_amount) || 0), 0);
      const debit = filtered.reduce((sum, r) => sum + (Number(r.debit_amount) || 0), 0);

      setTotals({ credit, debit, balance: debit - credit });

      // Compute the running balance once, in document order, independent of
      // Table pagination (column render's `index` resets every page, so it
      // cannot be used to slice the dataset for a cumulative total).
      let runningBalance = 0;
      const withRunningBalance = filtered.map((r) => {
        runningBalance += (Number(r.debit_amount) || 0) - (Number(r.credit_amount) || 0);
        return { ...r, running_balance: runningBalance };
      });

      setData(withRunningBalance);
    };

    filterData();
  }, [formdata, Alldata, searchTerm]);

  const getStatusTag = (record) => {
    const balance = (Number(record.debit_amount) || 0) - (Number(record.credit_amount) || 0);
    const recordDate = parseISO(record.date);
    const today = new Date();
    const daysDiff = (today - recordDate) / (1000 * 60 * 60 * 24);

    if (balance <= 0) return <Tag color="success">Completed</Tag>;
    if (daysDiff > 30) return <Tag color="error">Overdue</Tag>;
    return <Tag color="warning">Pending</Tag>;
  };

  const columns = [
    {
      title: "Txn ID",
      dataIndex: "transaction_id",
      key: "transaction_id",
      render: (v) => <Text strong style={{ color: "#1677ff" }}>{v}</Text>,
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (v) => formatDisplayDate(v),
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => getStatusTag(record),
    },
    {
      title: "A/C Type",
      dataIndex: "account_type",
      key: "account_type",
      render: (v) => <Tag>{v}</Tag>,
    },
    {
      title: formdata.showOnlyFinalBalance ? "Customer Name" : "A/C ID",
      key: "account_id",
      render: (_, record) => (
        formdata.showOnlyFinalBalance
          ? <Text strong>{record.customer_name || 'Unknown Customer'}</Text>
          : record.account_id
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: { showTitle: false },
      render: (v) => <Text ellipsis={{ tooltip: v }} style={{ maxWidth: 220, display: "inline-block" }}>{v}</Text>,
    },
    {
      title: "Debit",
      dataIndex: "debit_amount",
      key: "debit_amount",
      align: "right",
      render: (v) => v ? <Text style={{ color: "#cf1322" }}>฿{Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Text> : '-',
    },
    {
      title: "Credit",
      dataIndex: "credit_amount",
      key: "credit_amount",
      align: "right",
      render: (v) => v ? <Text style={{ color: "#389e0d" }}>฿{Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Text> : '-',
    },
    {
      title: "Balance",
      key: "balance",
      align: "right",
      render: (_, record) => {
        const runningBalance = formdata.showOnlyFinalBalance
          ? (record.final_balance || ((Number(record.debit_amount) || 0) - (Number(record.credit_amount) || 0)))
          : (record.running_balance ?? 0);
        return <Text strong style={{ color: runningBalance >= 0 ? "#1677ff" : "#d48806" }}>฿{runningBalance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Text>;
      },
    },
    {
      title: "Adjust",
      key: "adjust",
      align: "center",
      render: (_, record) => (
        record.transaction_id === "BALANCE_SUMMARY" ? null : (
          <Button size="small" icon={<EditOutlined />} onClick={() => openAdjustModal(record)}>
            Adjust
          </Button>
        )
      ),
    },
  ];

  return (
    <Layout>
      <Header title="Customer Ledger Management" />
      <ToastContainer position="top-right" autoClose={3000} />

      <div style={{ padding: 20 }}>
        {/* Summary Cards */}
        <Row gutter={[16, 16]}>
          <Col xs={12} md={6}>
            <Card>
              <Statistic
                title="Total Credit"
                value={Totals.credit}
                precision={2}
                prefix={<><span>฿</span> <ArrowUpOutlined /></>}
                valueStyle={{ color: "#389e0d" }}
              />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card>
              <Statistic
                title="Total Debit"
                value={Totals.debit}
                precision={2}
                prefix={<><span>฿</span> <ArrowDownOutlined /></>}
                valueStyle={{ color: "#cf1322" }}
              />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card>
              <Statistic
                title="Net Balance"
                value={Totals.balance}
                precision={2}
                prefix="฿"
                valueStyle={{ color: Totals.balance >= 0 ? "#1677ff" : "#d48806" }}
              />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card>
              <Statistic
                title="Total Records"
                value={data.length}
                prefix={<WalletOutlined />}
                valueStyle={{ color: "#722ed1" }}
              />
            </Card>
          </Col>
        </Row>

        {/* Toolbar */}
        <Card style={{ marginTop: 16 }}>
          <Row gutter={[12, 12]} align="middle" justify="space-between">
            <Col flex="auto" style={{ minWidth: 260 }}>
              <Space>
                <Text strong style={{ fontSize: 15 }}>Customer Ledger</Text>
                <Tag color="purple">{data.length} Records</Tag>
              </Space>
            </Col>
            <Col>
              <Space wrap>
                <Button icon={<FilterOutlined />} type={showFilters ? "primary" : "default"} onClick={() => setShowFilters(!showFilters)}>
                  Filters
                </Button>
                <Button icon={<FilePdfOutlined />} danger onClick={exportToPDF}>PDF</Button>
                <Button icon={<FileExcelOutlined />} onClick={exportToCSV} style={{ color: "#389e0d", borderColor: "#389e0d" }}>CSV</Button>
                <Button icon={<ReloadOutlined />} onClick={resetFilters}>Reset</Button>
              </Space>
            </Col>
          </Row>

          <Row style={{ marginTop: 16 }}>
            <Col span={24}>
              <Input
                size="large"
                prefix={<SearchOutlined />}
                placeholder="Search by Transaction ID, Description, Customer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                allowClear
              />
            </Col>
          </Row>

          {showFilters && (
            <Row gutter={[16, 16]} style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid #f0f0f0" }}>
              <Col xs={24} md={10}>
                <Text type="secondary" style={{ fontSize: 12 }}>Date Range</Text>
                <RangePicker
                  style={{ width: "100%" }}
                  format="YYYY-MM-DD"
                  allowEmpty={[true, true]}
                  value={[formdata.from ? dayjs(formdata.from) : null, formdata.to ? dayjs(formdata.to) : null]}
                  onChange={(_, dateStrings) => setFormData({ ...formdata, from: dateStrings?.[0] || "", to: dateStrings?.[1] || "" })}
                />
              </Col>
              <Col xs={24} md={8}>
                <Text type="secondary" style={{ fontSize: 12 }}><UserOutlined /> Customer</Text>
                <Select
                  showSearch
                  allowClear
                  style={{ width: "100%" }}
                  placeholder="All Customers"
                  value={formdata.account_id || undefined}
                  onChange={(value, option) => setFormData({
                    ...formdata,
                    account_id: value || "",
                    customername: option?.label || "",
                  })}
                  options={customerdata.map(c => ({ value: c.id, label: c.name }))}
                  filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
                />
              </Col>
              <Col xs={24} md={6}>
                <Text type="secondary" style={{ fontSize: 12, display: "block" }}>Final Balance Only</Text>
                <Space>
                  <Switch
                    checked={formdata.showOnlyFinalBalance}
                    onChange={(checked) => setFormData({ ...formdata, showOnlyFinalBalance: checked })}
                  />
                  <Text style={{ fontSize: 12 }} type="secondary">One row per customer</Text>
                </Space>
              </Col>
            </Row>
          )}
        </Card>

        {/* Data Table */}
        <Card style={{ marginTop: 16 }}>
          <Table
            rowKey={(row, index) => row.id || index}
            columns={columns}
            dataSource={data}
            loading={loading}
            pagination={{ pageSize: 15, showSizeChanger: true, showTotal: (t) => `${t} records` }}
            scroll={{ x: true }}
            locale={{ emptyText: (
              <div style={{ padding: "32px 0" }}>
                <ExclamationCircleOutlined style={{ fontSize: 32, color: "#bfbfbf" }} />
                <div style={{ marginTop: 8, color: "#8c8c8c" }}>No records found matching your criteria</div>
              </div>
            ) }}
          />
        </Card>
      </div>

      <Modal
        title="Adjust Ledger Entry"
        open={adjustModalOpen}
        onCancel={closeAdjustModal}
        onOk={handleSaveAdjustment}
        okText="Save Adjustment"
        okButtonProps={{ loading: adjustSaving }}
        confirmLoading={adjustSaving}
      >
        {adjustingRecord && (
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            <Row gutter={[12, 8]}>
              <Col span={12}>
                <Text type="secondary" style={{ fontSize: 12 }}>Txn ID</Text>
                <div><Text strong>{adjustingRecord.transaction_id}</Text></div>
              </Col>
              <Col span={12}>
                <Text type="secondary" style={{ fontSize: 12 }}>Date</Text>
                <div><Text strong>{formatDisplayDate(adjustingRecord.date)}</Text></div>
              </Col>
              <Col span={24}>
                <Text type="secondary" style={{ fontSize: 12 }}>Description</Text>
                <div><Text>{adjustingRecord.description}</Text></div>
              </Col>
            </Row>

            <div>
              <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 6 }}>Adjustment Type</Text>
              <Radio.Group value={adjustType} onChange={(e) => setAdjustType(e.target.value)}>
                <Radio.Button value="debit">Debit</Radio.Button>
                <Radio.Button value="credit">Credit</Radio.Button>
              </Radio.Group>
              <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 6 }}>
                Setting this entry as {adjustType === "debit" ? "Debit" : "Credit"} will clear the other amount to ฿0.00.
              </Text>
            </div>

            <div>
              <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 6 }}>
                {adjustType === "debit" ? "Debit Amount" : "Credit Amount"}
              </Text>
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                precision={2}
                prefix="฿"
                value={adjustAmount}
                onChange={(value) => setAdjustAmount(value || 0)}
              />
            </div>
          </Space>
        )}
      </Modal>
    </Layout>
  );
}
